import 'swiper/css';
import 'swiper/css/free-mode';
import type { ImageWithLabel } from '../../types';
import type { Swiper as SwiperType } from 'swiper';

import { useState } from 'react';
import { css, styled } from 'styled-components';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useSwiperRtl } from '../../hooks/useSwiperRtl';
import { Figure } from '../figure/Figure';
import { deviceWidth } from '../theme/theme';

const shadowAfter = css`
  &:after {
    content: '';
    display: block;
    top: 0;
    width: 2rem;
    height: 100%;
    position: absolute;
    z-index: 2;
    inset-inline-end: 0;
    background: linear-gradient(
      ${(props) => (props.theme.isRtl ? '-270deg' : '270deg')},
      #f2f3f4 0%,
      rgba(242, 243, 244, 0) 100%
    );
  }
`;

const SwiperSC = styled(Swiper)<{ $disableShadow: boolean }>`
  ${(props) => !props.$disableShadow && shadowAfter}
`;

const DocumentBorderWrapperSC = styled.div`
  border: 1px solid ${(props) => props.theme.colors.slateGray[50]};
  border-radius: 1rem;
  position: relative;
`;

export const DocumentWrapperSC = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 1rem;
  border: 4px solid ${(props) => props.theme.colors.white};

  @media (min-width: ${(props) => props.theme.devices.laptop}) {
    border-width: 8px;
  }
`;

type HorizontalDocumentsProps = {
  'data-test-id'?: string;
  pages: { back?: ImageWithLabel; front?: ImageWithLabel; unknown?: ImageWithLabel };
};

export const DocumentSwiper = ({ 'data-test-id': dataTestId, pages }: HorizontalDocumentsProps) => {
  const { handleOnSwiper, textDirection } = useSwiperRtl();
  const [isSwiperEnd, setIsSwiperEnd] = useState(false);

  const handleOnProgress = (swiper: SwiperType) => {
    setIsSwiperEnd(swiper.isEnd);
  };

  const slidesPerView = pages?.front?.image && pages?.back?.image ? 1.2 : 1;

  const breakPoints = {
    [deviceWidth.mobileS]: {
      slidesPerView,
    },
    [deviceWidth.tablet]: {
      slidesPerView: 2,
    },
  };

  return (
    <SwiperSC
      key={`${slidesPerView}-${textDirection}`}
      $disableShadow={isSwiperEnd}
      breakpoints={breakPoints}
      data-test-id={dataTestId}
      freeMode
      modules={[FreeMode]}
      onProgress={handleOnProgress}
      onSwiper={handleOnSwiper}
      spaceBetween={10}
    >
      {Object.values(pages).map(({ image, label }, id) => {
        if (image) {
          return (
            <SwiperSlide key={label || id}>
              <Figure key={label} label={label || ''}>
                <DocumentBorderWrapperSC>
                  <DocumentWrapperSC alt={`Document ${label} Page`} src={image} />
                </DocumentBorderWrapperSC>
              </Figure>
            </SwiperSlide>
          );
        }

        return null;
      })}
    </SwiperSC>
  );
};
